(() => {
  'use strict';

  const selectors = {
    tab: '[data-tab]',
    tabsHolder: '[data-tabs-holder]',
    tabsLink: '[data-tabs-link]',
    template: 'template',
  };

  /**
   * Keeps a tab's markup out of the DOM until the tab is opened for the first time.
   *
   * Wrap the contents of any non-default `[data-tab]` in `<deferred-tab><template>`
   * and the browser skips parsing and rendering them on load.
   *
   * The listener sits on the tabs holder in the capture phase deliberately: `tabs-component`
   * binds its own click handler to the nav link and runs `triggerTabAnimations()` and
   * `handleTabSliders()` against the tab immediately, so the markup has to be in place
   * before any listener on the link itself gets to run.
   */
  class DeferredTab extends HTMLElement {
    connectedCallback() {
      this.tab = this.closest(selectors.tab);
      this.holder = this.closest(selectors.tabsHolder);

      // Nothing to wait for outside a tabs holder
      if (!this.tab || !this.holder) {
        this.hydrate();
        return;
      }

      this.tabId = this.tab.getAttribute('data-tab');
      this.onActivateEvent = (event) => this.onActivate(event);

      this.holder.addEventListener('click', this.onActivateEvent, true);
      this.holder.addEventListener('keyup', this.onActivateEvent, true);
    }

    disconnectedCallback() {
      this.unbind();
    }

    onActivate(event) {
      const link = event.target.closest?.(selectors.tabsLink);

      if (!link || link.getAttribute('data-tabs-link') !== this.tabId) return;

      this.hydrate();
    }

    hydrate() {
      const template = this.querySelector(selectors.template);

      this.unbind();

      if (template) this.replaceWith(template.content);
    }

    unbind() {
      if (!this.onActivateEvent) return;

      this.holder.removeEventListener('click', this.onActivateEvent, true);
      this.holder.removeEventListener('keyup', this.onActivateEvent, true);
      this.onActivateEvent = null;
    }
  }

  if (!customElements.get('deferred-tab')) {
    customElements.define('deferred-tab', DeferredTab);
  }
})();
